import classnames from 'classnames';
import { Box, Grid, Typography, Chip } from '@material-ui/core';
import { VisibilityOff, WarningRounded } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import React, { memo, ReactNode } from 'react';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { useUser } from '../../../../../global/auth/useUser';
import { GetIncomingVolunteerAllTasks_vm_application_relevantTasks_roles as RoleType } from '../__generated__/GetIncomingVolunteerAllTasks';
import { RoleIcon } from '../../../../../common/icons/RoleIconList';

type IncomingVolunteerTaskListItemType = {
  taskId: string;
  icon: ReactNode;
  taskTitle: string;
  titleIcon?: ReactNode;
  taskSubText?: string;
  taskRecentActivityStatus?: string;
  onClick?: () => void;
  disabled?: boolean;
  selected?: boolean;
  adminOnly: boolean;
  roleReq?: RoleType[];
  taskType?: 'referee-check' | 'event-task' | 'manager-completed' | 'user-completed';
  lockedFromVolunteer?: boolean;
  blocksProgress?: boolean;
};

const useStyles = () => {
  const { theme, isMobile } = useCampfireTheme();
  return makeStyles({
    groupHeader: {
      boxSizing: 'border-box',
      width: '100%',
      padding: isMobile ? '0 14px' : '0 18px',
    },
    groupHeaderText: {
      fontWeight: 600,
      fontSize: '16px',
      color: theme.color.grey.neutralBrand800,
      display: 'flex',
      alignItems: 'center',
      padding: '.6em 0',
    },
    taskListGroup: {
      boxSizing: 'border-box',
      width: '100%',
      backgroundColor: '#ffffff',
      border: `1px solid ${theme.color.grey.border}`,
      borderRadius: 8,
      overflow: 'hidden',
      boxShadow: '0px 1px 2px rgba(114, 114, 114, 0.08), 0px 1px 3px rgba(214, 214, 214, 0.18)',
    },
    taskListItem: {
      boxSizing: 'border-box',
      backgroundColor: '#ffffff',
      transition: 'background-color 120ms ease',
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.color.grey.border}`,
      },
    },
    taskListItemHover: {
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.color.grey.background,
      },
    },
    taskListDisabled: {
      opacity: '0.5',
      cursor: 'default',
    },
    taskListItemSelected: {
      backgroundColor: theme.palette.grey[200],
    },
    taskListSubItem: {
      boxSizing: 'border-box',
      padding: isMobile ? '10px 14px' : '10px 18px',
    },
    iconContainer: {
      marginRight: isMobile ? 16 : 8,
    },
    borderBottom: {
      borderBottom: `1px solid ${theme.color.grey.border}`,
    },
    borderRight: {
      borderRight: `1px solid ${theme.color.grey.border}`,
    },
    taskListStack: {
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      width: '100%',
      padding: isMobile ? '0 12px 12px' : '0 18px 16px',
    },
  })();
};

type IncomingVolunteerTaskListItemProps = IncomingVolunteerTaskListItemType & { locked?: boolean };

const IncomingVolunteerTaskListItem = memo(
  ({
    icon,
    taskTitle,
    titleIcon,
    taskSubText,
    taskRecentActivityStatus,
    onClick,
    locked,
    disabled,
    selected,
    adminOnly,
    roleReq,
    taskType,
    lockedFromVolunteer,
    blocksProgress,
  }: IncomingVolunteerTaskListItemProps) => {
    const classes = useStyles();
    const {
      user: {
        userIdentityService: { isVmAdmin },
      },
    } = useUser();

    const onlyAdminDisabledClick = !isVmAdmin && adminOnly;
    const { theme } = useCampfireTheme();

    return (
      <Box
        display='flex'
        alignContent='center'
        alignItems='center'
        width={1}
        paddingX={1}
        className={`${classes.taskListItem} ${
          disabled || locked ? classes.taskListDisabled : classes.taskListItemHover
        } ${selected ? classes.taskListItemSelected : ''}`}
        onClick={() => (!locked && !disabled && !onlyAdminDisabledClick && onClick ? onClick() : null)}
      >
        <Box
          display='flex'
          alignContent='center'
          alignItems='center'
          flex={2}
          className={classes.borderRight}
          paddingY={2}
        >
          <Box display='flex' paddingX={2}>
            {icon}
          </Box>
          <Box>
            <span>
              <Box display='flex' alignItems='center' flexWrap='wrap' marginBottom={0.5}>
                <Typography variant='body2' style={{ display: 'flex', alignItems: 'center' }}>
                  {titleIcon}
                  {taskTitle}
                </Typography>
                {/* Lock indicator for tasks not visible to volunteers */}
                {lockedFromVolunteer && (
                  <Box display='flex' alignItems='center' marginLeft={1}>
                    <VisibilityOff style={{ fontSize: '16px', color: '#666', marginRight: '4px' }} />
                    <Typography style={{ fontSize: '11px', color: '#666', fontWeight: 500 }}>
                      Not visible to volunteer
                    </Typography>
                  </Box>
                )}
                {blocksProgress && (
                  <Chip
                    icon={<WarningRounded style={{ fontSize: '14px', color: '#E65100' }} />}
                    label='Blocking'
                    size='small'
                    style={{
                      backgroundColor: '#FFF4E6',
                      color: '#E65100',
                      fontSize: '0.7rem',
                      fontWeight: 550,
                      borderRadius: '4px',
                      height: '20px',
                      marginLeft: '8px',
                    }}
                  />
                )}
                {taskType && (
                  <Chip
                    label={
                      taskType === 'referee-check'
                        ? 'Referee Check'
                        : taskType === 'event-task'
                        ? 'Event'
                        : taskType === 'manager-completed'
                        ? 'Manager Completes'
                        : 'User Completes'
                    }
                    size='small'
                    style={{
                      backgroundColor:
                        taskType === 'referee-check'
                          ? '#f3e5f5'
                          : taskType === 'event-task'
                          ? '#e0f2f1'
                          : taskType === 'manager-completed'
                          ? '#FFF4E6'
                          : '#E3F2FD',
                      color:
                        taskType === 'referee-check'
                          ? '#7b1fa2'
                          : taskType === 'event-task'
                          ? '#00695c'
                          : taskType === 'manager-completed'
                          ? '#E65100'
                          : '#1565C0',
                      fontSize: '0.7rem',
                      fontWeight: 550,
                      borderRadius: '4px',
                      height: '20px',
                      marginLeft: '8px',
                    }}
                  />
                )}
                {roleReq
                  ? roleReq.map((role) => {
                      return (
                        <Box
                          key={role.activityRoleId}
                          style={{
                            backgroundColor: '#e7e7e7',
                            padding: '3px 5px',
                            borderRadius: '3px',
                            minHeight: '5px',
                            marginLeft: '8px',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {role?.icon ? (
                            <RoleIcon
                              iconName={role?.icon}
                              color={theme.color.grey.neutral300}
                              style={{ height: '12px' }}
                            />
                          ) : (
                            ''
                          )}
                          <Typography style={{ fontWeight: 600, fontSize: '11px', color: '#444', paddingLeft: '3px' }}>
                            {role?.name}
                          </Typography>
                        </Box>
                      );
                    })
                  : undefined}
              </Box>
              {taskSubText && (
                <Typography display='inline' variant='caption' color='textSecondary'>
                  {taskSubText}
                </Typography>
              )}
            </span>
          </Box>
        </Box>
        <Box display='flex' alignContent='center' alignItems='center' flex={1} paddingLeft={2}>
          <Typography variant='caption' color='textSecondary'>
            {taskRecentActivityStatus}
          </Typography>
        </Box>
      </Box>
    );
  }
);

const IncomingVolunteerTaskListItemMobile = memo(
  ({
    icon,
    taskTitle,
    titleIcon,
    taskSubText,
    taskRecentActivityStatus,
    onClick,
    locked,
    adminOnly,
    taskType,
    lockedFromVolunteer,
    blocksProgress,
  }: {
    icon: ReactNode;
    taskTitle: string;
    titleIcon?: ReactNode;
    taskSubText?: string;
    taskRecentActivityStatus?: string;
    onClick?: () => void;
    locked?: boolean;
    adminOnly: boolean;
    taskType?: 'referee-check' | 'event-task' | 'manager-completed' | 'user-completed';
    lockedFromVolunteer?: boolean;
    blocksProgress?: boolean;
  }) => {
    const classes = useStyles();
    const {
      user: {
        userIdentityService: { isVmAdmin },
      },
    } = useUser();

    const onlyAdminDisabledClick = !isVmAdmin && adminOnly;
    return (
      <Grid
        item
        container
        wrap='nowrap'
        alignItems='center'
        className={classnames(classes.taskListItem, classes.taskListSubItem)}
        onClick={() => (!locked && !onlyAdminDisabledClick && onClick ? onClick() : null)}
      >
        <Grid item className={classes.iconContainer}>
          {icon}
        </Grid>
        <Grid item container direction='column'>
          <Box display='flex' alignItems='center' flexWrap='wrap' marginBottom={0.5}>
            <Typography variant='body2' style={{ display: 'flex', alignItems: 'center' }}>
              {titleIcon}
              {taskTitle}
            </Typography>
            {/* Lock indicator for tasks not visible to volunteers */}
            {lockedFromVolunteer && (
              <Box display='flex' alignItems='center' marginLeft={1}>
                <VisibilityOff style={{ fontSize: '14px', color: '#666', marginRight: '3px' }} />
                <Typography style={{ fontSize: '10px', color: '#666', fontWeight: 500 }}>Not visible</Typography>
              </Box>
            )}
            {blocksProgress && (
              <Chip
                icon={<WarningRounded style={{ fontSize: '12px', color: '#E65100' }} />}
                label='Blocking'
                size='small'
                style={{
                  backgroundColor: '#FFF4E6',
                  color: '#E65100',
                  fontSize: '0.7rem',
                  fontWeight: 550,
                  borderRadius: '4px',
                  height: '18px',
                  marginLeft: '4px',
                }}
              />
            )}
            {taskType && (
              <Chip
                label={
                  taskType === 'referee-check'
                    ? 'Referee Check'
                    : taskType === 'event-task'
                    ? 'Event'
                    : taskType === 'manager-completed'
                    ? 'Manager Completes'
                    : 'User Completes'
                }
                size='small'
                style={{
                  backgroundColor:
                    taskType === 'referee-check'
                      ? '#f3e5f5'
                      : taskType === 'event-task'
                      ? '#e0f2f1'
                      : taskType === 'manager-completed'
                      ? '#FFF4E6'
                      : '#E3F2FD',
                  color:
                    taskType === 'referee-check'
                      ? '#7b1fa2'
                      : taskType === 'event-task'
                      ? '#00695c'
                      : taskType === 'manager-completed'
                      ? '#E65100'
                      : '#1565C0',
                  fontSize: '0.7rem',
                  fontWeight: 550,
                  borderRadius: '4px',
                  height: '18px',
                }}
              />
            )}
          </Box>
          {taskSubText && (
            <Grid item style={{ flex: 1 }}>
              <Typography variant='caption' color='textSecondary'>
                {taskSubText}
              </Typography>
            </Grid>
          )}
          <Grid item>
            <Typography variant='caption' color='textSecondary'>
              {taskRecentActivityStatus}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

export const IncomingVolunteerTaskList = ({
  header,
  headerAction,
  locked,
  items,
}: {
  header: string;
  headerAction?: ReactNode;
  locked?: boolean;
  items: Array<IncomingVolunteerTaskListItemType>;
}) => {
  const classes = useStyles();
  const { isMobile } = useCampfireTheme();
  return (
    <Grid container style={{ opacity: locked ? 0.5 : 1 }}>
      <Box display='flex' alignItems='center' justifyContent='space-between' className={classes.groupHeader}>
        <Typography className={classes.groupHeaderText}>{header}</Typography>
        {headerAction}
      </Box>
      {items.length > 0 && (
        <Box className={classes.taskListStack}>
          <Box className={classes.taskListGroup}>
            {items.map((item) =>
              isMobile ? (
                <IncomingVolunteerTaskListItemMobile
                  key={item.taskId}
                  icon={item.icon}
                  taskTitle={item.taskTitle}
                  titleIcon={item.titleIcon}
                  taskSubText={item.taskSubText}
                  taskRecentActivityStatus={item.taskRecentActivityStatus}
                  onClick={item.onClick}
                  locked={locked}
                  adminOnly={item.adminOnly}
                  taskType={item.taskType}
                  lockedFromVolunteer={item.lockedFromVolunteer}
                  blocksProgress={item.blocksProgress}
                />
              ) : (
                <IncomingVolunteerTaskListItem
                  key={item.taskId}
                  icon={item.icon}
                  taskId={item.taskId}
                  taskTitle={item.taskTitle}
                  titleIcon={item.titleIcon}
                  taskSubText={item.taskSubText}
                  taskRecentActivityStatus={item.taskRecentActivityStatus}
                  onClick={item.onClick}
                  locked={locked}
                  disabled={item.disabled}
                  selected={item.selected}
                  adminOnly={item.adminOnly}
                  roleReq={item.roleReq}
                  taskType={item.taskType}
                  lockedFromVolunteer={item.lockedFromVolunteer}
                  blocksProgress={item.blocksProgress}
                />
              )
            )}
          </Box>
        </Box>
      )}
    </Grid>
  );
};
